/**
 * UseTestThresholdsState
 *
 * Manages threshold state for the test detail form.
 * Derives thresholdsType from overrideThreshold + dynamicThresholds (mirrors portal/ui prepareThresholds()).
 * Resolves metric titles/units from testType.metrics.
 * Registers virtual RHF fields (thresholdsSensitivity, _thresholdsDirty) that are converted back
 * to API fields by UseSubmitTransforms.thresholdsTransform.
 *
 * thresholdsType is managed by NgpRadioGroup's Controller (registered via Controller, not register()).
 * thresholdsSensitivity is manually registered via register() (no Controller renders it).
 *
 * IMPORTANT: The thresholds array itself is NOT managed via RHF watch/setValue.
 * In RHF v6 with shouldUnregister: true, setValue on unregistered fields has no persistent
 * effect — the value is never stored and watch always returns the initial value from reset().
 * Instead, local useState drives the display, and thresholdsRef.current holds the current
 * values for the submit transform.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { t } from '@lingui/macro';
import { cloneDeep, first, get, isArray, isEmpty, keyBy, sortBy, upperFirst } from 'lodash';
import { SHOULD_DIRTY_FALSE, SHOULD_DIRTY_TRUE } from '../../ServiceTestsFormControlConstants';

const THRESHOLD_SENSITIVITY_DEFAULT = 4;

export const MANUAL = 'manual';
export const GLOBAL = 'global';
export const AUTOMATIC = 'automatic';

const THRESHOLDS_TYPE = 'thresholdsType';
const THRESHOLDS_SENSITIVITY = 'thresholdsSensitivity';
const THRESHOLDS_DIRTY = '_thresholdsDirty';

/**
 * Derive the UI radio value from API fields.
 * Mirrors portal/ui testsDetails.controller.js prepareThresholds() (lines 424-447).
 */
function deriveThresholdsType(formData) {
    if (!formData?.overrideThreshold) return GLOBAL;
    return formData.dynamicThresholds ? AUTOMATIC : MANUAL;
}

/**
 * Sort thresholds by first metricId and attach the original array index as formIndex.
 * formIndex preserves the original array position for ref-based updates.
 */
function sortWithFormIndex(thresholds) {
    return (thresholds || [])
        .map((th, originalIndex) => ({ ...th, formIndex: originalIndex }))
        .sort((a, b) => (first(a.metricIds) || 0) - (first(b.metricIds) || 0));
}

/**
 * Convert underscore-separated metric name to Title Case.
 * Mirrors portal/ui UtilityService.makeKeyPretty → toTitleCase.
 * e.g. "response_time" → "Response Time", "jitter" → "Jitter"
 */
function makeKeyPretty(name) {
    if (!name) return '';
    const normalized = name.replace(/_/g, ' ').trim();
    if (!normalized.includes(' ')) {
        return upperFirst(normalized);
    }
    return normalized.replace(/\w\S*/g, (word) => upperFirst(word.toLowerCase()));
}

/**
 * Resolve a metric title from testType.metrics, mirroring portal/ui formatMetricName().
 * Priority: metric.title → i18n lookup → lqmos_avg special case → makeKeyPretty fallback.
 *
 * portal/ui source: serviceTestThresholdConfig.directive.js:69-75
 */
// camelCase metric names that makeKeyPretty cannot convert correctly
const METRIC_NAME_OVERRIDES = {
    avgResponse: 'Response Time',
};

function resolveMetricTitle(metric) {
    if (!metric) return '';
    if (metric.title) {
        return metric.title;
    }

    const name = metric.name || '';
    if (METRIC_NAME_OVERRIDES[name]) {
        return METRIC_NAME_OVERRIDES[name];
    }
    // Handle VoIP metric name — mirrors portal/ui: if (name === 'lqmos_avg') return 'MOS';
    if (name === 'lqmos_avg') {
        return 'MOS';
    }
    return makeKeyPretty(name);
}

/**
 * @param {object} params
 * @param {object} params.testType - Current test type configuration
 * @param {object} params.formData - Loaded form data from API (via useFindById + reset)
 * @param {React.MutableRefObject} params.thresholdsRef - Ref holding current threshold
 *   values for the submit transform. Created in TestDetailPanel, populated here.
 */
export default function useTestThresholdsState({ testType, formData, thresholdsRef }) {
    const { register, setValue, watch } = useFormContext();
    const initializedRef = useRef(false);

    // --- Local state for threshold display (replaces watch('thresholds')) ---
    // RHF v6 + shouldUnregister:true cannot reliably update unregistered array fields
    // via setValue/watch. useState guarantees re-renders on every change.
    const [sortedFormThresholds, setSortedFormThresholds] = useState([]);

    // --- Metric resolution (mirrors portal/ui metricsById = keyBy(testType.metrics, 'id')) ---
    const metricsById = useMemo(() => keyBy(testType?.metrics || [], 'id'), [testType?.metrics]);

    // --- Global thresholds from testType (read-only, for Global mode display) ---
    const globalThresholds = useMemo(
        () => sortBy(cloneDeep(testType?.thresholds || []), (th) => first(th.metricIds)),
        [testType?.thresholds]
    );

    // --- Whether automatic thresholds radio should be visible ---
    const allowAutomaticThresholds = !!testType?.allowAutomaticThresholds;

    // --- Global automatic threshold sensitivity (for disabled slider in Global mode) ---
    const globalAutomaticSensitivity = useMemo(
        () => get(testType, 'automaticThreshold.sensitivity', THRESHOLD_SENSITIVITY_DEFAULT),
        [testType]
    );
    const hasGlobalAutomaticThreshold = !!testType?.automaticThreshold;

    // --- Register virtual RHF fields on mount ---
    // thresholdsType: managed by NgpRadioGroup Controller — do NOT register here.
    // thresholdsSensitivity: no Controller, manual registration keeps it in form state.
    // _thresholdsDirty: sentinel field to mark form dirty when thresholds are edited.
    useEffect(() => {
        register({ name: THRESHOLDS_SENSITIVITY });
        register({ name: THRESHOLDS_DIRTY });
    }, [register]);

    // --- Initialize from loaded form data (once per mount) ---
    // Safe to use one-time guard because TestDetailPanel uses key={testId-dupOfId}
    // which forces remount on record change.
    useEffect(() => {
        if (initializedRef.current || !formData || !testType?.thresholds) {
            return;
        }
        initializedRef.current = true;

        // portal/ui source: testsDetails.controller.js line 430 falls back to testType.thresholds
        // when test.thresholds is absent (create mode). Clone to avoid aliasing with formData.
        const sourceThresholds = !isEmpty(formData.thresholds) ? formData.thresholds : testType.thresholds;
        thresholdsRef.current = cloneDeep(sourceThresholds || []);
        setSortedFormThresholds(sortWithFormIndex(thresholdsRef.current));

        const derivedType = deriveThresholdsType(formData);
        const derivedSensitivity = formData.thresholdsSensitivity ?? THRESHOLD_SENSITIVITY_DEFAULT;

        setValue(THRESHOLDS_TYPE, derivedType, SHOULD_DIRTY_FALSE);
        setValue(THRESHOLDS_SENSITIVITY, derivedSensitivity, SHOULD_DIRTY_FALSE);
    }, [formData, testType, setValue, thresholdsRef]);

    // --- Watch registered fields (thresholdsType via Controller, thresholdsSensitivity via register) ---
    const thresholdsType = watch(THRESHOLDS_TYPE) || GLOBAL;
    const thresholdsSensitivity = watch(THRESHOLDS_SENSITIVITY) ?? THRESHOLD_SENSITIVITY_DEFAULT;

    // --- Metric label resolver ---
    const getMetricLabel = useMemo(
        () => (metricIds) => {
            const metric = metricsById[first(metricIds)];
            return resolveMetricTitle(metric) || t`Unknown Metric`;
        },
        [metricsById]
    );

    // --- Metric unit resolver ---
    const getMetricUnit = useMemo(
        () => (metricIds) => {
            const metric = metricsById[first(metricIds)];
            return metric?.units || '';
        },
        [metricsById]
    );

    // --- Handlers ---
    const handleSensitivityChange = useCallback(
        (_event, sliderValue) => {
            const nextValue = isArray(sliderValue) ? first(sliderValue) : sliderValue;
            setValue(THRESHOLDS_SENSITIVITY, parseFloat(nextValue) || THRESHOLD_SENSITIVITY_DEFAULT, {
                shouldDirty: true,
            });
        },
        [setValue]
    );

    const handleThresholdChange = useCallback(
        (formIndex, field, value) => {
            // Update ref for submit transform
            const current = Array.isArray(thresholdsRef.current) ? thresholdsRef.current : [];
            thresholdsRef.current = current.map((th, i) => (i === formIndex ? { ...th, [field]: value } : th));

            // Update display state (sorted array with formIndex mapping)
            setSortedFormThresholds((prev) =>
                prev.map((th) => (th.formIndex === formIndex ? { ...th, [field]: value } : th))
            );

            // Mark form dirty via sentinel field so Save button activates
            setValue(THRESHOLDS_DIRTY, Date.now(), SHOULD_DIRTY_TRUE);
        },
        [setValue, thresholdsRef]
    );

    return {
        thresholdsType,
        thresholdsSensitivity,
        allowAutomaticThresholds,
        globalThresholds,
        hasGlobalAutomaticThreshold,
        globalAutomaticSensitivity,
        sortedFormThresholds,
        getMetricLabel,
        getMetricUnit,
        handleSensitivityChange,
        handleThresholdChange,
    };
}
